import type { AccessScope } from '../../auth';
import type { EquipmentDispatchProjection } from './model';
import { featureAccessProjectionStatus } from '../../feature-access-projection';
import { canReadHeadquartersFactoryFeature } from '../../headquarters-factory-read';

const activeSitePersonas = new Set([
  'system-admin',
  'site-manager',
  'safety-manager',
  'site-staff',
  'safety-staff',
  'partner-admin',
]);
const controllerPersonas = new Set(['site-manager', 'safety-manager', 'safety-staff']);

export function equipmentDispatchProjection(
  scope: AccessScope,
  selectedScopeId = scope.id,
): EquipmentDispatchProjection | null {
  // A fallback scope can keep the shell usable while an auth refresh settles, but it must never
  // grant a site-bound dispatch surface for a different selected scope.
  if (scope.id !== selectedScopeId) return null;
  if (canReadHeadquartersFactoryFeature(scope, 'equipment-dispatch')) return 'reader';
  const feature = featureAccessProjectionStatus(scope.featureAccess);
  if (feature.kind === 'malformed' || feature.kind === 'enforced' && !feature.features.has('equipment-dispatch')) return null;
  if (feature.kind === 'enforced' && scope.developerReadOnly) return null;
  if (!activeSitePersonas.has(scope.persona)) return null;
  if (scope.resource.kind === 'tenant') return null;
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(scope.resource.siteId)) return null;
  // Navigation follows the approved session's site membership, not a capability array that can lag
  // behind a policy release. The server still rereads membership and equipment binding.
  if (
    scope.resource.kind === 'site' &&
    (controllerPersonas.has(scope.persona) ||
      scope.capabilities.includes('equipment-dispatch:control'))
  )
    return 'controller';
  return 'requester';
}
